import type { FilterItem } from "@via/components/advanced-filter/helpers/types";
import {
  BLOCK_ORGANIZATION_STEPS,
  KEYS,
  LANGUAGE_MODES,
  ROUTES,
} from "@via/utils/constants.tsx";
import { OperatorMap } from "@via/utils/interfaces.tsx";
import {
  DataSourceProvider,
  Permissions,
  PermissionType,
  RevisionType,
  RegistrationType,
} from "common";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { hasActiveMembership } from "@via/hooks/organization-access.ts";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

export function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(" ");
}

// This is a simple helper to determine the current page by comparing pathname / route
export const isCurrent = (item: string, pathname: string): boolean => {
  const page: string = item.replace(/\s+/g, "-").toLowerCase();
  if (
    page === ROUTES.PIPELINES &&
    pathname.includes(KEYS.PROJECTS) &&
    pathname.includes(KEYS.PIPELINES)
  ) {
    return false;
  }
  return pathname.includes(page);
};

export const formatDateString = (
  date: string | Date,
  displayFormat: string = "YYYY-MM-DD"
): string => {
  /**
   * Important: dayjs(...) formats ISO strings in the user's local timezone by default.
   * For date-only fields stored as UTC-midnight (e.g. "2020-12-01T00:00:00.000Z"),
   * local timezone conversion can show the previous day.
   *
   * Heuristic: if the input looks like a UTC ISO timestamp (contains 'T' and ends with 'Z')
   * and the requested display format does NOT include time-of-day tokens, format in UTC.
   */
  if (typeof date === "string") {
    const s = date.trim();
    const looksLikeUtcIso = s.includes("T") && s.endsWith("Z");
    // Localized formats LT, LTS, LLL, LLLL, lll and llll carry a time of day too.
    const formatIncludesTime =
      /[HhmsSAaZ]/.test(displayFormat) || /LT|LLL|lll/.test(displayFormat);
    if (looksLikeUtcIso && !formatIncludesTime) {
      return dayjs.utc(s).format(displayFormat);
    }
  }

  return dayjs(date).format(displayFormat);
};

/**
 * Formats a date into two lines: date on first line, time on second line.
 * Useful for displaying dates in tables where space is limited.
 * Uses dayjs for robust date formatting that works across different locales.
 *
 * @param date - Date string or Date object
 * @returns JSX element with date and time on separate lines, or "-" if no date
 *
 * @example
 * formatDateTimeTwoLines(new Date())
 * // Returns:
 * // <span>
 * //   October 14, 2025
 * //   <br />
 * //   3:30 PM
 * // </span>
 */
export const formatDateTimeTwoLines = (
  date: string | Date | null | undefined
): JSX.Element => {
  if (!date) return <span>-</span>;

  try {
    const dayjsDate = dayjs(date);

    // Check if date is valid
    if (!dayjsDate.isValid()) {
      return <span>-</span>;
    }

    // Format date part: "Month Day, Year" (e.g., "October 14, 2025")
    const datePart = dayjsDate.format("LL");

    // Format time part: "Hour:Minute AM/PM" (e.g., "3:30 PM")
    const timePart = dayjsDate.format("LT");

    return (
      <span>
        {datePart}
        <br />
        {timePart}
      </span>
    );
  } catch (error) {
    console.error("Error formatting date:", error);
    return <span>-</span>;
  }
};

/**
 * Calculates an estimate of the size of a JavaScript object in bytes by converting it to a JSON string.
 * Note: This method provides an approximation and may not be entirely accurate, especially for complex objects.
 *
 * @param {Object} obj - The JavaScript object to calculate the size of.
 * @returns {number} - The estimated size of the object in bytes.
 */
export function getObjectSizeInBytes(obj: object): number {
  const jsonString = JSON.stringify(obj);
  const bytes = new Blob([jsonString]).size;

  return bytes;
}

/**
 * Deeply compares two objects to determine if they are equal.
 *
 * This function performs a recursive comparison of the values within the objects,
 * taking into account nested structures. It handles various data types, including
 * objects, arrays, and primitive values.
 *
 * @param obj1 - The first object to compare.
 * @param obj2 - The second object to compare.
 * @returns {boolean} - Returns true if the objects are deeply equal, false otherwise.
 */
export function isDeepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }

    if (!isDeepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Removes falsy values from an object.
 * Falsy values include: false, null, undefined, 0, "", NaN.
 * @param {Object} obj - The input object.
 * @returns {Object} - An object with only truthy values.
 */
export function removeFalsyValues(obj: object): object {
  const filteredObj = {};

  for (const key in obj) {
    if (obj[key]) {
      filteredObj[key] = obj[key];
    }
  }

  return filteredObj;
}

/**
 * Removes empty strings from an object, including nested objects.
 * @param {Object} obj - The input object.
 * @returns {Object} - An object with empty strings removed.
 */
export function removeEmptyStrings(obj: object): object {
  const filteredObj = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    const value = obj[key];
    if (typeof value === "string") {
      if (value !== "") {
        filteredObj[key] = value;
      }
    } else if (typeof value === "object" && value !== null) {
      filteredObj[key] = removeEmptyStrings(value);
    } else {
      filteredObj[key] = value;
    }
  }

  return filteredObj;
}

export const isNotEqualArray = (first, second): boolean => {
  return JSON.stringify(first) !== JSON.stringify(second);
};

export const uniqBy = <T,>(
  items: T[],
  keyFn: (item: T) => string | number | boolean | null | undefined
): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (key === null || typeof key === "undefined") continue;
    const normalizedKey = String(key);
    if (!normalizedKey || seen.has(normalizedKey)) continue;
    seen.add(normalizedKey);
    out.push(item);
  }
  return out;
};

export const isObjectEmpty = (object): boolean => {
  return Object.keys(object).length === 0 && object.constructor === Object;
};

export const decode = (string: string): string => {
  return atob(string);
};

// A utility function to debounce any given function
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
) => {
  let timeout: NodeJS.Timeout;
  return function (this: void, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Function to infer programming language based on filename
export const getLanguageByFileExtension = (filename: string) => {
  // Mapping of extensions and filenames to languages
  const extensionToLanguage = {
    ".py": LANGUAGE_MODES.PYTHON,
    ".pl": LANGUAGE_MODES.PERL,
    ".sh": LANGUAGE_MODES.SHELL,
    ".config": LANGUAGE_MODES.GROOVY,
    ".nf": LANGUAGE_MODES.GROOVY,
    ".groovy": LANGUAGE_MODES.GROOVY,
    ".md": LANGUAGE_MODES.MARKDOWN,
    ".rmd": LANGUAGE_MODES.MARKDOWN,
    ".yaml": LANGUAGE_MODES.YAML,
    ".yml": LANGUAGE_MODES.YAML,
    ".r": LANGUAGE_MODES.R,
    dockerfile: LANGUAGE_MODES.DOCKER,
  };

  // Convert the filename to lowercase and look for the last extension separator
  const lowerFilename = filename.toLowerCase();
  const extension = lowerFilename.includes(".")
    ? lowerFilename.slice(lowerFilename.lastIndexOf("."))
    : lowerFilename;

  // Return the language or "groovy" if not found
  return extensionToLanguage[extension] || LANGUAGE_MODES.GROOVY;
};

// Type guard to ensure value is an object
export const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const getPermissionType = permission => {
  switch (permission) {
    case PermissionType.Public:
      return PermissionType.Public;
    case PermissionType.GroupShared:
      return PermissionType.GroupShared;
    case PermissionType.UserOwned:
      return PermissionType.UserOwned;
    default:
      return PermissionType.UserOwned;
  }
};

export const getPermission = (permissionType: string) => {
  switch (permissionType) {
    case PermissionType.Public:
      return Permissions.Public;
    case PermissionType.GroupShared:
      return Permissions.GroupShared;
    case PermissionType.UserOwned:
    default:
      return Permissions.UserOwned;
  }
};

/**
 * Converts a snake_case string to camelCase.
 *
 * @param {string} str - The snake_case string to convert.
 * @returns {string} - The converted camelCase string.
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Recursively converts all keys in an object from snake_case to camelCase.
 *
 * @param {any} obj - The object to convert.
 * @returns {any} - The object with keys converted to camelCase.
 */
export function snakeToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamel(item));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = snakeToCamel(obj[key]);
      return acc;
    }, {} as any);
  } else {
    return obj;
  }
}

/** A helper that can either take an array of keys to remove from a payload,
 * and / or remove all empty strings and null values
 * @param payload
 * @param keysToRemove
 */
export function removeKeysAndEmptyStrings(
  payload: Record<string, any>,
  keysToRemove: string[]
) {
  // Remove specified keys
  keysToRemove.forEach(key => {
    delete payload[key];
  });

  // Remove properties with empty string values / null / undefined
  Object.keys(payload).forEach(key => {
    if (
      payload[key] === "" ||
      payload[key] === null ||
      payload[key] === undefined
    ) {
      delete payload[key];
    }
  });

  return payload;
}

/**
 * True when the user has supplied nothing for a field.
 *
 * `0` and `false` are answers, not blanks: a required input set to either is
 * provided, and treating it as missing wrongly blocks a run launch. Note that
 * an empty array or object still reads as provided; callers that must detect
 * "nothing selected" have to check that themselves.
 */
export function isFalsyOrEmpty(value) {
  return value == null || value === "" || Number.isNaN(value);
}

/**
 * Recursively converts all values within an object or array to strings.
 *
 * @param {Record<string, unknown> | unknown[]} obj - The object or array whose values will be converted to strings.
 *                                                    The object can contain nested objects or arrays, which will
 *                                                    also have their values converted.
 * @returns {Record<string, string> | string[]} A new object or array with the same structure as the input,
 *                                              but with all values converted to strings.
 */
export const convertObjValuesToString = (
  obj: Record<string, unknown> | unknown[]
): Record<string, string> | string[] => {
  if (Array.isArray(obj)) {
    return obj.map(value => {
      if (isObject(value)) {
        return convertObjValuesToString(value) as Record<string, string>;
      } else if (Array.isArray(value)) {
        return convertObjValuesToString(value) as string[];
      }
      // Handle undefined or null values explicitly
      return value === undefined || value === null ? "" : String(value);
    }) as string[]; // Ensures the array is explicitly typed as `string[]`
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (isObject(value)) {
        return [key, convertObjValuesToString(value) as Record<string, string>];
      } else if (Array.isArray(value)) {
        return [key, convertObjValuesToString(value) as string[]];
      }
      // Handle undefined or null values explicitly
      return [key, value === undefined || value === null ? "" : String(value)];
    })
  );
};

/** A helper function to get the elapsed time since a given date
 *  If the difference is more than 1 day, it returns the formatted date
 * */
export const getElapsedTime = (date: string): string => {
  if (dayjs().diff(dayjs(date), "days") > 1) {
    return formatDateString(date, "LLL");
  } else {
    return dayjs(date).fromNow();
  }
};

/**
 * Converts a value from bytes to gigabytes.
 *
 * @param {number} bytes - The size in bytes to be converted.
 * @returns {number} - Returns the size converted to gigabytes.
 */
export const bytesToGB = (bytes: number): number => {
  const bytesInGB = 1024 * 1024 * 1024;
  return bytes / bytesInGB;
};

/**
 * Checks if a given version string follows the semantic versioning format.
 *
 * @param {string} version - The version string to be checked.
 * @param {boolean} [flex=false] - Indicates whether to use a flexible version pattern.
 *                                 If true, allows versions like "1.2" or "1.2.3".
 * @returns {boolean} - Returns true if the version is valid according to the semantic versioning pattern, otherwise false.
 */
export const checkIsSemanticVersion = (
  version: string,
  flex: boolean = false
): boolean => {
  if (!version) return false;
  const semverRegex = flex ? /^(\d+)\.(\d+)(\.\d+)?$/ : /^(\d+)\.(\d+)\.(\d+)$/;
  return semverRegex.test(version);
};

/**
 * Calculates and returns the next semantic version based on the provided type.
 *
 * @param {RevisionType} type - The type of version bump:
 *                                      Patch, Minor, or Major.
 * @param {string} version - The current version string to be incremented.
 * @returns {string} - Returns the incremented semantic version string.
 *                     If the input version is invalid, returns "1.0.0".
 * @throws {Error} - Throws an error if the type is not Patch, Minor, or Major.
 */
export const calculateVersion = (
  type: RevisionType,
  version: string
): string => {
  if (!checkIsSemanticVersion(version, true)) {
    return "1.0.0";
  }

  let [major, minor, patch] = version.split(".").map(Number);

  switch (type) {
    case RevisionType.Patch:
      patch++;
      break;
    case RevisionType.Minor:
      minor++;
      patch = 0;
      break;
    case RevisionType.Major:
      major++;
      minor = 0;
      patch = 0;
      break;
    default:
      throw new Error("Invalid type. Type must be Patch, Minor, or Major.");
  }

  return `${major}.${minor}.${patch}`;
};

export const getDataCollectionLocationFields = (provider: number) => {
  switch (provider) {
    case DataSourceProvider.Amazon:
    case DataSourceProvider.Google:
      return "Bucket";
    case DataSourceProvider.GEO:
      return "";
    case DataSourceProvider.SSH:
    case DataSourceProvider.URL:
    default:
      return "Location";
  }
};

export const extractUrlPart = (url: string): string | null => {
  const match = url.match(/https?:\/\/([^/:]+)(?::\d+|\/|$)/);
  return match ? match[1] : null;
};

/**
 * Checks if a given string is a valid URL.
 * @param {string} value - The input string to validate.
 * @returns {boolean} True if the input is a valid URL, otherwise false.
 */
export const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Checks if a given URL string points to localhost.
 * @param {string} url - The URL string to check.
 * @returns {boolean} True if the URL hostname is localhost or 127.0.0.1, otherwise false.
 *
 * @example
 * isLocalhost("http://localhost:3000"); // true
 * isLocalhost("http://127.0.0.1:8080"); // true
 * isLocalhost("https://app.viafoundry.com"); // false
 */
export const isLocalhost = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname;
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
};

/**
 * Formats a file size from bytes into a human-readable string.
 *
 * Converts the given byte size into the most appropriate unit (Bytes, KB, MB, GB, TB)
 * with two decimal places.
 *
 * @param {number | string | null | undefined} bytes - The file size in bytes. If `null` or `undefined`, an empty string is returned.
 * @returns {string} A formatted string representing the file size in the best fitting unit.
 *
 * @example
 * ```ts
 * formatFileSize(1024); // "1.00 KB"
 * formatFileSize(1048576); // "1.00 MB"
 * formatFileSize(1073741824); // "1.00 GB"
 * formatFileSize(null); // ""
 * formatFileSize(0); // "0 Bytes"
 * ```
 */
export const formatFileSize = (
  bytes: number | string | null | undefined
): string => {
  if (bytes == null) {
    return "";
  }
  if (typeof bytes === "string") {
    const trimmed = bytes.trim();
    if (/^\d+(\.\d+)?\s?(B|Bytes|KB|MB|GB|TB)$/i.test(trimmed)) {
      return trimmed;
    }

    const parsed = Number(trimmed);
    if (!isNaN(parsed)) {
      bytes = parsed;
    } else {
      return "";
    }
  }
  if (bytes === 0) return "0 Bytes";
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Generic helper to create a MongoDB filter object from UI filters and operator map.
 *
 * @param filters Array of filter items with id and value
 * @param operatorMap Mapping of filter keys to MongoDB operators (e.g. "eq", "regex", "in", etc.)
 * @returns MongoDB filter object suitable for backend API
 */
export function createMongoFilter(
  filters:
    | FilterItem[]
    | { or: FilterItem[] }
    | { and: FilterItem[] }
    | undefined,
  operatorMap: OperatorMap
): Record<string, any> | undefined {
  // Return undefined if no filters provided
  if (!filters) {
    return undefined;
  }

  const mongoFilter: Record<string, any> = {};

  if ("or" in filters) {
    mongoFilter["$or"] = filters.or.map(subFilter =>
      createMongoFilter([subFilter], operatorMap)
    );
  } else if ("and" in filters) {
    mongoFilter["$and"] = filters.and.map(subFilter =>
      createMongoFilter([subFilter], operatorMap)
    );
  } else {
    filters.forEach(({ id, value }) => {
      const operator = operatorMap[id] || "regex"; // default to equality if no operator specified

      switch (operator) {
        case "regex":
          mongoFilter[id] = { regex: `${value}` };
          break;
        case "eq":
          mongoFilter[id] = { eq: value };
          break;
        case "in":
          mongoFilter[id] = Array.isArray(value)
            ? { in: value }
            : { in: [value] };
          break;
        case "not":
          mongoFilter[id] = Array.isArray(value)
            ? { nin: value }
            : { nin: [value] };
          break;
        case "exists":
          mongoFilter[id] = { exists: Boolean(value) };
          break;
        case "gt":
          mongoFilter[id] = { gt: value };
          break;
        case "gte":
          mongoFilter[id] = { gte: value };
          break;
        case "lt":
          mongoFilter[id] = { lt: value };
          break;
        case "lte":
          mongoFilter[id] = { lte: value };
          break;
        case "gte,lte": {
          const { startDate, endDate } = value as {
            startDate: Date;
            endDate: Date;
          };
          mongoFilter[id] = { gte: startDate, lte: endDate };
          break;
        }
        default:
          mongoFilter[id] = { eq: value };
      }
    });
  }

  return mongoFilter;
}

/** Formats a number as a currency string.
 *
 * @param {number} amount - The amount to format, in cents.
 * @param {string} [currency="usd"] - The currency code (default is "usd").
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (
  amount: number,
  currency: string = "usd",
  minimumFractionDigits: number = 0
): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits,
  }).format(amount / 100);

/**
 * Recursively retrieves all field paths that are marked as dirty (true) in a nested fields object.
 * @param {Record<string, any>} fields - The object containing field paths and their dirty status.
 * @param {string} [parentKey=""] - The parent key to prepend to the field paths.
 * @return {Set<string>} A set of field paths that are marked as dirty.
 *
 */
export const getDirtyFieldPaths = (
  fields: Record<string, any>,
  parentKey = ""
): Set<string> => {
  const paths = new Set<string>();

  const traverse = (obj: Record<string, any>, prefix: string) => {
    Object.entries(obj).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value === true) {
        paths.add(path);
      } else if (isObject(value)) {
        traverse(value, path);
      }
    });
  };

  traverse(fields, parentKey);
  return paths;
};

export enum UserOrganizationRoleEnum {
  OrgAdmin = "org_admin",
  Member = "member",
  Viewer = "viewer",
}

export function getReadableOrganizationRoleByRoleName(
  role: UserOrganizationRoleEnum
): string {
  switch (role) {
    case UserOrganizationRoleEnum.OrgAdmin:
      return "Organization Admin";
    case UserOrganizationRoleEnum.Viewer:
      return "Viewer";
    case UserOrganizationRoleEnum.Member:
    default:
      return "Member";
  }
}

export const shouldBlockAccess = user => {
  if (user?.registrationType === RegistrationType.Guest) {
    return BLOCK_ORGANIZATION_STEPS.includes(user?.onboardingStep);
  }
  return (
    (user?.registrationType !== null &&
      user?.registrationType !== RegistrationType.Guest &&
      BLOCK_ORGANIZATION_STEPS.includes(user?.onboardingStep)) ||
    // An inactive first membership decides only when no membership is active (#1134).
    (!hasActiveMembership(user) &&
      user?.userOrganizations?.[0]?.status === "inactive" &&
      user?.userOrganizations?.[0]?.role?.name === "org_admin")
  );
};

export const shouldBlockOrgMemberAccess = user => {
  return (
    !hasActiveMembership(user) &&
    user?.userOrganizations?.[0]?.status === "inactive" &&
    user?.userOrganizations?.[0]?.role?.name !== "org_admin"
  );
};

export const downloadCSV = (columns: any[], data: any[]) => {
  const headers = columns.map(col => col.accessorKey);
  const csvContent = [
    headers.join(","),
    ...data.map(row =>
      headers
        .map(header => {
          const value = row[header] || ""; // Get the value or empty string if undefined
          return `"${value.toString().replace(/"/g, '""')}"`; // Escape double quotes
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "data.csv";
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Normalize a data-source path based on provider.
// If Amazon -> ensure "s3://", if Google -> ensure "gs://"
export const normalizeProviderPath = (
  provider: DataSourceProvider | undefined,
  rawPath: string | undefined
): string => {
  if (!provider || !rawPath) return rawPath ?? "";

  const path = rawPath.trim();

  if (provider === DataSourceProvider.Amazon) {
    // If it already starts with s3:// (case-insensitive), return as is
    if (/^s3:\/\//i.test(path)) return path;
    return `s3://${path.replace(/^\/+/, "")}`;
  }

  if (provider === DataSourceProvider.Google) {
    // If it already starts with gs:// (case-insensitive), return as is
    if (/^gs:\/\//i.test(path)) return path;
    return `gs://${path.replace(/^\/+/, "")}`;
  }

  // fallback for other providers
  return path;
};

/**
 * Truncates a string in the middle if it exceeds a given maximum length.
 *
 * Keeps the beginning and end parts of the text, replacing the removed middle section
 * with an ellipsis (`…`). Useful for file names or identifiers where both start and end
 * segments carry meaning.
 *
 * @param {string} text - The input text to truncate.
 * @param {number} [maxLength=30] - The maximum allowed length of the output string.
 * @returns {string} The truncated string with a middle ellipsis if needed.
 *
 * @example
 * truncateMiddle("verylongfilenameexample.txt", 15);
 * // Returns: "verylongf…example.txt"
 */
export const truncateMiddle = (text: string, maxLength = 30): string => {
  if (text.length <= maxLength) return text;
  const half = Math.floor(maxLength / 2);
  const first = text.slice(0, half);
  const last = text.slice(-half);
  return `${first}…${last}`;
};

/**
 * Checks if a string is a valid date string (either date-only or datetime format).
 */
export const isValidDateString = (dateStr: string): boolean => {
  // Accept both date-only (YYYY-MM-DD) and datetime formats
  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
  const dateTimeRegex =
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  return (
    (dateOnlyRegex.test(dateStr) || dateTimeRegex.test(dateStr)) &&
    !isNaN(Date.parse(dateStr))
  );
};

/**
 * Formats a date into a human-readable string.
 * @param timestamp - The timestamp to format.
 * @returns The formatted date string.
 */
export const formatDateAgoString = (timestamp: number): string => {
  const date = dayjs(timestamp);
  const mins = dayjs().diff(date, "minute");
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.floor(mins / 60 / 24)}d ago`;
  return date.format("l");
};

/**
 * Formats a duration in milliseconds into a human-readable string.
 * @param ms - Duration in milliseconds.
 * @returns "Xms" for sub-second values, "X.Xs" otherwise.
 */
export const formatDurationMs = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export const getAxiosErrorMessage = (error: unknown): string | undefined => {
  const anyErr = error as
    | {
        response?: {
          data?: { error?: string; message?: string } | string;
        };
        message?: unknown;
      }
    | undefined
    | null;
  const data = anyErr?.response?.data;
  if (typeof data === "string") return data;
  return data?.error || data?.message || String(anyErr?.message);
};

/**
 * The `message` of a failed request's response body, for an onError notification.
 *
 * A request that fails at the network level (connection dropped, request blocked, a proxy that
 * never answers) rejects with no `response` at all. Reading `error.response.data` there throws
 * a TypeError inside onError, before setNotification runs, so the user sees nothing (#930).
 * This returns undefined in that case, and for a body without a non-empty string `message`, so
 * the caller's `?? "fallback"` always reaches the notification.
 */
export const getErrorResponseMessage = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  const message = (data as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" && message.trim() !== ""
    ? message
    : undefined;
};

export const getBlobErrorMessage = async (
  error: unknown
): Promise<string | undefined> => {
  const blob = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  if (!(blob instanceof Blob)) {
    const message = getAxiosErrorMessage(error);
    return message && message !== "undefined" ? message : undefined;
  }

  try {
    const text = (await blob.text()).trim();
    if (!text) return undefined;

    try {
      const parsed = JSON.parse(text) as { message?: unknown; error?: unknown };
      if (typeof parsed.message === "string" && parsed.message.trim() !== "") {
        return parsed.message;
      }
      if (typeof parsed.error === "string" && parsed.error.trim() !== "") {
        return parsed.error;
      }
    } catch {
      return text;
    }

    return text;
  } catch {
    const message = getAxiosErrorMessage(error);
    return message && message !== "undefined" ? message : undefined;
  }
};

// FileReader rather than `blob.text()`: jsdom's Blob has no `text()`, and
// `new Response(blob).text()` there resolves to "[object Blob]", not the body.
const readBlobAsText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * The `message` of an error response body. A request made with
 * `responseType: "blob"` gets its error body back as a Blob too, so the
 * `{ message }` that expressErrorHandlerMiddleware (backend/src/lib/error.ts)
 * sends is not at `error.response.data.message` until the Blob is read.
 *
 * Returns undefined unless the body carries a non-empty string `message`, so
 * the caller keeps its own fallback for a body that is not JSON, such as a
 * proxy's HTML error page. That is the difference from getBlobErrorMessage
 * above, which shows such a body as raw text.
 */
export const getErrorBodyMessage = async (
  error: unknown
): Promise<string | undefined> => {
  const data = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  if (!(data instanceof Blob)) return getErrorResponseMessage(error);
  try {
    return getErrorResponseMessage({
      response: { data: JSON.parse(await readBlobAsText(data)) },
    });
  } catch {
    return undefined;
  }
};
